import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { GitFork } from 'lucide-react';
import { forkedPurchaseOrders, type PurchaseVendorLine } from '../../utils/purchaseLineVendors';
import { requisitionTotal } from '../../utils/purchasingLineItems';
import './PurchasePoForkBadge.css';

type ForkLine = PurchaseVendorLine & {
  quantity?: number;
  unitPrice?: number;
  taxable?: boolean;
  taxRate?: number;
  taxAmount?: number;
};

const money = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

function trunc(value: string, max: number): string {
  const text = value.trim();
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}

const PAD = 8;
const ROOT_W = 168;
const GROUP_W = 214;
const GAP_X = 52;
const GAP_Y = 10;
const NODE_H = 62;

function edgePath(fromY: number, toY: number, rootX: number): string {
  const x1 = rootX + ROOT_W;
  const y1 = fromY + NODE_H / 2;
  const x2 = rootX + ROOT_W + GAP_X;
  const y2 = toY + NODE_H / 2;
  const cx = (x1 + x2) / 2;
  return `M ${x1} ${y1} C ${cx} ${y1}, ${cx} ${y2}, ${x2 - 1} ${y2}`;
}

function ForkDiagram({
  prNumber,
  forks,
  salesTaxPct,
  markerId,
}: {
  prNumber: string;
  forks: Array<{ vendorName: string; poNumber: string; lines: ForkLine[] }>;
  salesTaxPct: number;
  markerId: string;
}) {
  const height = PAD * 2 + forks.length * NODE_H + Math.max(0, forks.length - 1) * GAP_Y;
  const width = PAD + ROOT_W + GAP_X + GROUP_W + PAD;
  const rootY = PAD + Math.max(0, (height - PAD * 2 - NODE_H) / 2);
  const rootX = PAD;
  return (
    <svg
      className="po-forkdiag"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={`${prNumber} issued ${forks.length} purchase orders`}
    >
      <defs>
        <marker id={markerId} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M 0 0 L 10 5 L 0 10 z" />
        </marker>
      </defs>
      {forks.map((fork, index) => {
        const y = PAD + index * (NODE_H + GAP_Y);
        return (
          <path
            key={fork.poNumber}
            className="po-forkdiag-edge"
            d={edgePath(rootY, y, rootX)}
            markerEnd={`url(#${markerId})`}
          />
        );
      })}
      <g className="po-forkdiag-node po-forkdiag-node--root" transform={`translate(${rootX} ${rootY})`}>
        <rect width={ROOT_W} height={NODE_H} rx={7} />
        <text className="po-forkdiag-kicker" x={10} y={18}>Requisition</text>
        <text className="po-forkdiag-title" x={10} y={36}>{trunc(prNumber, 18)}</text>
        <text className="po-forkdiag-sub" x={10} y={52}>One approval</text>
      </g>
      {forks.map((fork, index) => {
        const y = PAD + index * (NODE_H + GAP_Y);
        const x = PAD + ROOT_W + GAP_X;
        const amount = requisitionTotal(
          fork.lines.map(line => ({
            quantity: Number(line.quantity) || 0,
            unitPrice: Number(line.unitPrice) || 0,
            taxable: line.taxable !== false,
            taxRate: line.taxRate,
            taxAmount: line.taxAmount,
          })),
          salesTaxPct,
        );
        const lineLabel = `${fork.lines.length} line${fork.lines.length === 1 ? '' : 's'}`;
        return (
          <g key={fork.poNumber} className="po-forkdiag-node po-forkdiag-node--po" transform={`translate(${x} ${y})`}>
            <rect width={GROUP_W} height={NODE_H} rx={7} />
            <text className="po-forkdiag-title" x={10} y={20}>
              <title>{fork.vendorName || 'Vendor not set'}</title>
              {trunc(fork.vendorName || 'Vendor not set', 26)}
            </text>
            <text className="po-forkdiag-sub" x={10} y={36}>{fork.poNumber}</text>
            <text className="po-forkdiag-meta" x={10} y={52}>{lineLabel}</text>
            <text className="po-forkdiag-amount" x={GROUP_W - 10} y={52} textAnchor="end">{money(amount)}</text>
          </g>
        );
      })}
    </svg>
  );
}

const PurchasePoForkBadge: React.FC<{
  prNumber: string;
  lines: ForkLine[];
  headerVendor?: string | null;
  headerPo?: string | null;
  salesTaxPct?: number;
}> = ({ prNumber, lines, headerVendor, headerPo, salesTaxPct = 0 }) => {
  const forks = forkedPurchaseOrders(lines, headerVendor, headerPo);
  const markerId = `po-fork-${useId().replace(/:/g, '')}`;
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const closeTimer = useRef<number | null>(null);

  const show = useCallback(() => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    const rect = wrapRef.current?.getBoundingClientRect();
    if (rect) {
      const width = Math.min(480, window.innerWidth - 16);
      const estimated = 92 + forks.length * (NODE_H + GAP_Y);
      const below = rect.bottom + 6;
      const top = below + estimated > window.innerHeight - 8
        ? Math.max(8, rect.top - estimated - 6)
        : below;
      setPos({
        top,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
      });
    }
    setOpen(true);
  }, [forks.length]);

  const hide = useCallback(() => {
    closeTimer.current = window.setTimeout(() => setOpen(false), 120);
  }, []);

  useEffect(() => () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
  }, []);

  if (forks.length < 2) return null;

  return (
    <span
      ref={wrapRef}
      className="po-fork-badge-wrap"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onClick={event => event.stopPropagation()}
      onKeyDown={event => event.stopPropagation()}
    >
      <button
        type="button"
        className="po-fork-badge"
        aria-label={`This requisition issued ${forks.length} purchase orders`}
        aria-expanded={open}
        onClick={event => {
          event.stopPropagation();
          if (open) setOpen(false);
          else show();
        }}
      >
        <GitFork size={11} aria-hidden />
        {forks.length}
      </button>
      {open && pos
        ? createPortal(
            <div
              className="po-fork-card"
              role="dialog"
              aria-label={`How ${prNumber} became ${forks.length} purchase orders`}
              style={{ top: pos.top, left: pos.left }}
              onMouseEnter={show}
              onMouseLeave={hide}
              onClick={event => event.stopPropagation()}
            >
              <header className="po-fork-card-head">
                <GitFork size={13} aria-hidden />
                <span>
                  <strong>One requisition → {forks.length} purchase orders</strong>
                </span>
              </header>
              <ForkDiagram prNumber={prNumber} forks={forks} salesTaxPct={salesTaxPct} markerId={markerId} />
              <p className="po-fork-card-note">
                Lines for the same store share a purchase order. Each order is one vendor.
              </p>
            </div>,
            document.body,
          )
        : null}
    </span>
  );
};

export default PurchasePoForkBadge;
